/**
 * `GET /captacao` (TP-025): os tickets de triagem que entraram na esteira.
 *
 * `ticket` tem a forma EXATA de uma linha de `GET /triagem`, então a camada
 * anticorrupção é a da fila (`mapearTicketDaTriagem`) — mesma validação de
 * enum e de data, mesma disciplina de nunca ecoar valor de campo em erro.
 */

import type { CaptacaoEntradasResponse } from '@anery/api-contracts';

import { requisitar } from '../../../../shared/api/clienteHttp';
import { DataInvalida, parseInstante, type Instante } from '../../../../shared/dominio/datas';
import { ErroDeContratoDeTriagem, mapearTicketDaTriagem } from '../../../../shared/triagem/mapeadores';
import type { TicketDaTriagem } from '../../../../shared/triagem/tipos';

export interface EntradaNaCaptacao {
  readonly entrouEm: Instante;
  /**
   * `enteredAt` como veio, para o `desde` do polling. O servidor guarda
   * microssegundos e `Date` só guarda milissegundos: um `desde` remontado a
   * partir de `entrouEm` seria anterior à entrada, e ela voltaria em toda
   * rodada.
   */
  readonly cursor: string;
  readonly ticket: TicketDaTriagem;
}

export function mapearEntradasNaCaptacao(resposta: CaptacaoEntradasResponse): EntradaNaCaptacao[] {
  if (!Array.isArray(resposta.items)) {
    throw new ErroDeContratoDeTriagem('items', 'esperado um array de entradas');
  }

  return resposta.items.map((item, indice) => {
    let entrouEm: Instante;
    try {
      entrouEm = parseInstante(item.enteredAt);
    } catch (causa) {
      if (causa instanceof DataInvalida) {
        throw new ErroDeContratoDeTriagem(`items[${String(indice)}].enteredAt`, causa.message);
      }
      throw causa;
    }
    return { entrouEm, cursor: item.enteredAt, ticket: mapearTicketDaTriagem(item.ticket) };
  });
}

/** `desde` = o `cursor` da última entrada vista; `null` na carga da tela. */
export function buscarEntradasNaCaptacao(desde: string | null, sinal: AbortSignal): Promise<EntradaNaCaptacao[]> {
  const busca = desde === null ? '' : `?${new URLSearchParams({ desde }).toString()}`;
  return requisitar<CaptacaoEntradasResponse>(`/captacao${busca}`, { sinal }).then(mapearEntradasNaCaptacao);
}
